import {
  getDashboardList,
  createDashboard,
  deleteDashboard,
  duplicateDashboard,
  createMagicLink,
  disableMagicLink,
} from "@/api/dashboards";
import Console from "@/models/Console.model";
import { types } from "./mutations";
import omit from "lodash/omit";

const actions = {
  /**
   * Method to fetch all dashboards
   * Returns the dashboard list if present in the datasource else makes API request
   * @param
   */
  fetchConsoles: async ({ commit, state, getters }) => {
    // check if enties list is non empty
    if (state.consolesById && state.consolesById.length > 0)
      return {
        status: 200,
        data: {
          data: getters.getConsoles,
        },
      };

    const { data, status } = await getDashboardList();
    // save the dashboard list in store
    if (status === 200 && data && data.dashboards.length > 0) {
      const consoles = data.dashboards.reduce((result, dashboard) => {
        const { cohorts } = dashboard;

        let metrics = [];
        if (cohorts && cohorts.length > 0) {
          cohorts.forEach(cohort => {
            if (cohort && cohort.metrics && cohort.metrics.length > 0) {
              metrics.push(
                ...cohort.metrics.map(item => ({
                  metric_id: item.id,
                  name: item.name,
                }))
              );
            }
          });
        }
        const newConsole = new Console({ ...dashboard, metrics });
        result[newConsole.id] = newConsole;
        return result;
      }, {});
      commit(types.SET_DASHBOARDS, consoles);
      return consoles;
    } else {
      commit(types.SET_DASHBOARDS, {});
      return null;
    }
  },

  /**
   * Creates console with the specified configuration
   * @param {*} param0
   * @param {*} data
   */
  createDashboard: async ({ dispatch }, data) => {
    return new Promise((resolve, reject) => {
      createDashboard(data)
        .then(response => {
          if (response.status === 200) {
            dispatch("reset");
          }
          return resolve(response);
        })
        .catch(error => {
          return reject(error);
        });
    });
  },

  updateConsoleData: async ({ state, commit }, { dashboard_id, data }) => {
    if (state.consoles && state.consoles[dashboard_id]) {
      commit(types.SET_DASHBOARDS, {
        ...state.consoles,
        [dashboard_id]: {
          ...state.consoles[dashboard_id],
          ...data,
        },
      });
    }
  },

  /**
   * Deletes a particular console based on it's dashboard_id
   * Sends an api request, if success, removes that dashboard from the store
   */
  deleteConsole: async ({ state, commit }, { dashboard_id }) => {
    try {
      const response = await deleteDashboard(dashboard_id);
      const { status, message } = response;
      if (status === 200) {
        if (state.consoles[dashboard_id]) {
          commit(types.SET_DASHBOARDS, omit(state.consoles, dashboard_id));
        }
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error(error);
      return false;
    }
  },

  /**
   * Duplicates a given dashboard with a new name
   * Sends an API request, if success,
   * creates a new entry in the store with the new id and name
   */
  duplicateConsole: async (
    { state, commit },
    { dashboard_id, name, description }
  ) => {
    try {
      const response = await duplicateDashboard(
        dashboard_id,
        name,
        description
      );
      const { status, data } = response;
      if (status === 200) {
        if (data && data.dashboard_id) {
          // new, duplicated console object
          const _consoleEntry = {
            [data.dashboard_id]: new Console({
              ...state.consoles[dashboard_id],
              name, // change the name and description
              description,
              id: data.dashboard_id, // and id to the new ID
            }),
          };
          // if success, mutate the state by adding a new item to the state object
          commit(
            types.SET_DASHBOARDS,
            Object.assign({}, state.consoles, _consoleEntry)
          );
        }
      }
    } catch (error) {
      console.log(error);
    }
  },

  createPublicLink: async ({ dispatch }, { dashboard_id: dash_id }) => {
    try {
      const response = await createMagicLink(dash_id);
      const { status, data } = response;
      if (!(status === 200 && data)) {
        throw new Error("Unable to create the magic link");
      }
      const { dashboard_id, public_url } = data;
      dispatch("updateConsoleData", {
        dashboard_id,
        data: { magic_link: public_url },
      });
      return {
        dashboard_id,
        public_url,
      };
    } catch (error) {
      console.error({ error });
      return null;
    }
  },

  disablePublicLink: async ({ dispatch }, { dashboard_id: dash_id }) => {
    try {
      const response = await disableMagicLink(dash_id);
      const { status, data } = response;
      if (!(status === 200 && data)) {
        throw new Error("Unable to create the magic link");
      }
      const { dashboard_id } = data;
      await dispatch("updateConsoleData", {
        dashboard_id,
        data: { magic_link: null },
      });
      return {
        dashboard_id,
      };
    } catch (error) {
      console.error(error);
      return null;
    }
  },
  /**
   * Resets the store with initial state
   * @param {Object} param0
   */
  reset: async ({ commit }) => {
    commit(types.reset);
  },
};

export default actions;
